/**
 * Turning findings into things that leave the portal: a spreadsheet, a printable
 * report, and a prompt for a coding agent.
 *
 * Kept apart from the view because these are pure functions over findings, which
 * makes them checkable on their own.
 */
import type { CodeFinding, CodeSnippet } from './types';

export const SEVERITIES = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFORMATIONAL'];

/**
 * Inspector writes its remediation advice in Markdown, which reads badly on paper
 * and worse in a spreadsheet cell, so links collapse to "text (url)".
 */
export function plainText(markdown: string | undefined): string {
  if (!markdown) return '';

  return markdown
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * A cell is quoted whenever it could otherwise break the row, and a leading
 * formula character is defused first.
 *
 * Findings carry text taken from the scanned repository, so a file path or title
 * starting with `=` or `+` would be evaluated as a formula when the owner opens the
 * export in Excel. Prefixing a tab keeps the value readable while making it inert.
 */
function csvCell(value: string | number | undefined): string {
  if (value === undefined || value === '') return '';

  const text = String(value);
  const safe = /^[=+\-@\t\r]/.test(text) ? `\t${text}` : text;

  return `"${safe.replace(/"/g, '""')}"`;
}

const CSV_COLUMNS: { header: string; value: (finding: CodeFinding) => string | number | undefined }[] = [
  { header: 'Severity', value: (f) => f.severity },
  { header: 'Scan type', value: (f) => f.category },
  { header: 'Finding', value: (f) => f.title },
  { header: 'Description', value: (f) => f.description },
  { header: 'Repository', value: (f) => f.repository },
  { header: 'File', value: (f) => f.fullFilePath ?? f.filePath },
  { header: 'Line', value: (f) => f.startLine },
  { header: 'Suggested fix', value: (f) => plainText(f.remediationText) },
  { header: 'Guidance', value: (f) => f.remediationUrl },
  { header: 'CWEs', value: (f) => f.cwes.join(' ') },
  { header: 'Vulnerability', value: (f) => f.vulnerabilityId },
  { header: 'Fix available', value: (f) => f.fixAvailable },
  { header: 'Detector', value: (f) => f.detectorName },
  { header: 'First seen', value: (f) => f.firstObservedAt },
  { header: 'Last seen', value: (f) => f.lastObservedAt },
];

export function downloadText(contents: string, filename: string, type: string) {
  const blob = new Blob([contents], { type: `${type};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * A findings report written as instructions for a coding agent.
 *
 * Ordered by severity, grouped by file, and carrying AWS's own recommendation for
 * each finding verbatim, because that is the authority the agent should follow rather
 * than whatever it would infer from the code alone. The line numbers come from the
 * scanned commit, so the prompt tells the agent to locate the code rather than trust
 * them — the file will usually have moved on by the time anyone runs this.
 */
export function buildFixPrompt(findings: CodeFinding[], snippets: Record<string, CodeSnippet>, scope: string): string {
  // A severity we do not know about sorts last rather than first, which is what
  // indexOf's -1 would otherwise do with UNTRIAGED.
  const rank = (severity: string) => {
    const index = SEVERITIES.indexOf(severity);
    return index === -1 ? SEVERITIES.length : index;
  };

  const ranked = [...findings].sort(
    (a, b) =>
      rank(a.severity) - rank(b.severity) || (a.fullFilePath ?? '').localeCompare(b.fullFilePath ?? ''),
  );

  const counts = SEVERITIES.map((value) => ({ value, count: ranked.filter((f) => f.severity === value).length }))
    .filter((entry) => entry.count > 0)
    .map((entry) => `${entry.count} ${entry.value.toLowerCase()}`)
    .join(', ');

  const lines = [
    '# Fix Amazon Inspector security findings',
    '',
    `Amazon Inspector code security scanned **${scope}** and reported ${ranked.length} finding${ranked.length === 1 ? '' : 's'}${counts ? ` (${counts})` : ''}.`,
    'Each finding below includes the file, the code as scanned, and the remediation AWS recommends.',
    '',
    '## How to work through these',
    '',
    '- Work in severity order: critical first, then high.',
    "- Treat the AWS recommendation as the intended fix. Where it conflicts with this repository's conventions, follow the conventions and say so.",
    '- Line numbers are from the commit that was scanned and may have shifted. Find the code by its content, and if you cannot find it, say so rather than guessing.',
    '- Fix the cause rather than silencing the detector. Do not add suppression comments or disable rules.',
    '- Some findings are false positives. If the code is already safe, explain why instead of changing it.',
    '- Never commit a real secret while fixing a hardcoded-credential finding. Move it to AWS Secrets Manager or an environment variable, and assume any exposed value needs rotating.',
    '- Keep each fix small and reviewable, and run the existing tests and type checks afterwards.',
    '',
    '---',
    '',
  ];

  ranked.forEach((finding, index) => {
    const snippet = snippets[finding.id];
    const location = finding.fullFilePath ?? finding.filePath ?? 'unknown file';
    const range = finding.startLine
      ? `${finding.startLine}${finding.endLine && finding.endLine !== finding.startLine ? `-${finding.endLine}` : ''}`
      : undefined;

    lines.push(
      `## ${index + 1}. ${finding.severity} — ${finding.title}`,
      '',
      `- **File:** \`${location}\`${range ? ` (line ${range} when scanned)` : ''}`,
      `- **Scan type:** ${finding.category}`,
    );

    if (finding.cwes.length > 0) lines.push(`- **CWE:** ${finding.cwes.join(', ')}`);
    if (finding.detectorName) lines.push(`- **Detector:** ${finding.detectorName}`);
    if (finding.vulnerabilityId) lines.push(`- **Vulnerability:** ${finding.vulnerabilityId}`);

    lines.push('', `**Problem.** ${plainText(finding.description)}`, '');

    if (snippet?.lines.length) {
      // The language hint is taken from the extension so the agent gets syntax
      // highlighting and a clue about the file type.
      const extension = location.split('.').pop() ?? '';
      lines.push('**Code as scanned:**', '', `\`\`\`${LANGUAGE_BY_EXTENSION[extension] ?? ''}`);
      for (const line of snippet.lines) lines.push(`${line.lineNumber}: ${line.content}`);
      lines.push('```', '');
    }

    if (finding.remediationText) {
      lines.push(`**AWS recommends.** ${plainText(finding.remediationText)}`, '');
    }

    if (finding.remediationUrl) lines.push(`Detector guidance: ${finding.remediationUrl}`, '');

    for (const fix of snippet?.suggestedFixes ?? []) {
      if (fix.description) lines.push(`**Suggested patch.** ${plainText(fix.description)}`, '');
      if (fix.code) lines.push('```diff', fix.code, '```', '');
    }

    lines.push('---', '');
  });

  return lines.join('\n');
}

const LANGUAGE_BY_EXTENSION: Record<string, string> = {
  ts: 'ts',
  tsx: 'tsx',
  js: 'js',
  jsx: 'jsx',
  py: 'python',
  java: 'java',
  go: 'go',
  rb: 'ruby',
  cs: 'csharp',
  tf: 'hcl',
  yml: 'yaml',
  yaml: 'yaml',
  json: 'json',
  sh: 'bash',
};

export function buildCsv(findings: CodeFinding[]): string {
  const rows = [
    CSV_COLUMNS.map((column) => csvCell(column.header)).join(','),
    ...findings.map((finding) => CSV_COLUMNS.map((column) => csvCell(column.value(finding))).join(',')),
  ];

  // The BOM is what makes Excel read it as UTF-8 rather than mangling accents.
  return `\uFEFF${rows.join('\r\n')}\r\n`;
}

export function downloadCsv(findings: CodeFinding[], filename: string) {
  downloadText(buildCsv(findings), filename, 'text/csv');
}
